import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppShellLayout from '../components/AppShellLayout';

interface ActionRecord {
  id: number;
  senderEmail: string | null;
  senderName: string | null;
  actionType: string;
  emailCount: number;
  undoSupported: boolean;
  undoneAt: string | null;
  performedAt: string;
}

const ACTION_LABELS: Record<string, string> = {
  archive: 'Archived',
  trash: 'Deleted',
  label: 'Labelled',
  unsubscribe: 'Unsubscribed',
  inbox_sweep: 'Inbox sweep',
};

export default function HistoryPage() {
  const { t } = useTranslation();
  const [actions, setActions] = useState<ActionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [undoing, setUndoing] = useState<number | null>(null);
  const [undoError, setUndoError] = useState<string | null>(null);
  const [sweepUndoPendingId, setSweepUndoPendingId] = useState<number | null>(null);

  const fetchActions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/actions');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setActions(await res.json());
    } catch {
      setError(t('history.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchActions(); }, [fetchActions]);

  async function undoAction(id: number, actionType: string) {
    setUndoing(id);
    setUndoError(null);
    try {
      const res = await fetch(`/api/actions/${id}/undo`, { method: 'POST' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setUndoError(data.error === 'reauth_required'
          ? 'Gmail reconnection required to undo.'
          : t('history.undoError'));
        return;
      }
      setActions(prev => prev.map(a =>
        a.id === id ? { ...a, undoneAt: new Date().toISOString() } : a
      ));
      if (actionType === 'inbox_sweep') {
        setSweepUndoPendingId(id);
      }
    } catch {
      setUndoError(t('history.undoError'));
    } finally {
      setUndoing(null);
    }
  }

  function formatDate(iso: string) {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }

  function senderDisplay(action: ActionRecord) {
    if (action.actionType === 'inbox_sweep') return 'All scanned emails';
    return action.senderName || action.senderEmail || '—';
  }

  return (
    <AppShellLayout>
      <div className="page-header">
        <h1>{t('history.title')}</h1>
        <p>{t('history.subtitle')}</p>
      </div>

      {undoError && (
        <div className="inline-banner inline-banner--error">
          {undoError}{' '}
          <button className="link-btn" onClick={() => setUndoError(null)}>Dismiss</button>
        </div>
      )}

      {sweepUndoPendingId !== null && (
        <div className="inline-banner inline-banner--info">
          Restoring your inbox &mdash; emails are being moved back from &ldquo;Email Helper &ndash; Sorting&rdquo;
          to your inbox. This may take a few minutes depending on how many emails were swept.{' '}
          <button className="link-btn" onClick={() => setSweepUndoPendingId(null)}>Dismiss</button>
        </div>
      )}

      {loading && (
        <div className="table-scroll-wrapper">
          <table className="senders-table">
            <thead>
              <tr>
                <th>Date</th><th>Sender</th><th>Action</th><th>Emails</th><th></th>
              </tr>
            </thead>
            <tbody>
              {[...Array(5)].map((_, i) => (
                <tr key={i}>
                  <td><div className="skeleton skeleton-text-sm" /></td>
                  <td><div className="skeleton skeleton-text" /></td>
                  <td><div className="skeleton skeleton-badge" /></td>
                  <td><div className="skeleton skeleton-badge" /></td>
                  <td></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {error && (
        <p className="state-message state-error">
          {error}{' '}
          <button className="link-btn" onClick={fetchActions}>{t('common.retry')}</button>
        </p>
      )}

      {!loading && !error && actions.length === 0 && (
        <div className="empty-state">
          <p>{t('history.empty')}</p>
        </div>
      )}

      {!loading && !error && actions.length > 0 && (
        <div className="table-scroll-wrapper">
          <table className="senders-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Sender</th>
                <th>Action</th>
                <th>Emails</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {actions.map(action => (
                <tr key={action.id} style={action.undoneAt ? { opacity: 0.5 } : undefined}>
                  <td style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                    {formatDate(action.performedAt)}
                  </td>
                  <td>{senderDisplay(action)}</td>
                  <td>
                    <span className="email-count-badge">
                      {ACTION_LABELS[action.actionType] ?? action.actionType}
                    </span>
                  </td>
                  <td>
                    {action.emailCount > 0 && (
                      <span className="email-count-badge">{action.emailCount}</span>
                    )}
                  </td>
                  <td>
                    {action.undoneAt ? (
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                        {sweepUndoPendingId === action.id ? 'Restoring…' : 'Undone ✓'}
                      </span>
                    ) : action.undoSupported ? (
                      <button
                        className="btn-secondary"
                        disabled={undoing === action.id}
                        onClick={() => undoAction(action.id, action.actionType)}
                      >
                        {undoing === action.id ? t('history.undoing') : t('history.undo')}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppShellLayout>
  );
}
